import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, RefreshCw, Search, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "@/lib/api";
import { useAdmin } from "@/context/AdminContext";
import { ChannelAccountAdmin } from "@/components/channel/ChannelAccountAdmin";

export const ChannelSupportInbox = () => {
  const [tickets, setTickets] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [reply, setReply] = useState("");
  const [query, setQuery] = useState("");
  const [folder, setFolder] = useState("open");
  const [busy, setBusy] = useState(false);
  const [bypassReason, setBypassReason] = useState("");
  const [linkedAccount, setLinkedAccount] = useState(null);
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountEmailDraft, setAccountEmailDraft] = useState("");
  const { isOwner } = useAdmin();

  const load = useCallback(() =>
    api.get("/channel/support/tickets")
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        setTickets(list);
        setSelectedId((current) => (current && list.some((ticket) => ticket.id === current) ? current : ""));
      })
      .catch((e) => toast.error(errText(e))), []);

  useEffect(() => {
    load();
    const refresh = setInterval(load, 30000);
    return () => clearInterval(refresh);
  }, [load]);

  const selected = tickets.find((ticket) => ticket.id === selectedId);
  useEffect(() => {
    setLinkedAccount(null);
    setBypassReason("");
    setAccountEmailDraft("");
  }, [selectedId]);

  useEffect(() => {
    if (selected?.status === "closed") {
      setLinkedAccount(null);
      setAccountEmailDraft("");
    }
  }, [selected?.id, selected?.status]);
  const needle = query.trim().toLowerCase();
  const openCount = tickets.filter((ticket) => ticket.status !== "closed").length;
  const closedCount = tickets.length - openCount;
  const folderTickets = tickets.filter((ticket) => folder === "closed" ? ticket.status === "closed" : ticket.status !== "closed");
  const visible = folderTickets.filter((ticket) => `${ticket.reference} ${ticket.name} ${ticket.email} ${ticket.subject}`.toLowerCase().includes(needle));

  const updateStatus = async (action) => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/channel/support/tickets/${selected.id}/${action}`, {});
      setTickets((current) => current.map((ticket) => ticket.id === selected.id ? data : ticket));
      if (action === "close") {
        setLinkedAccount(null);
        setAccountEmailDraft("");
      }
      toast.success(action === "resolve" ? "Ticket marked resolved" : action === "close" ? "Ticket closed" : "Ticket reopened");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const deleteTicket = async () => {
    if (!selected || selected.status !== "closed") return;
    if (!window.confirm(`Permanently delete ticket ${selected.reference}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/channel/support/tickets/${selected.id}`);
      setTickets((current) => current.filter((ticket) => ticket.id !== selected.id));
      setSelectedId("");
      toast.success("Ticket deleted");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const sendUpdateEmail = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(`/channel/support/tickets/${selected.id}/notify`, {});
      await load();
      toast.success("Update email sent to the customer");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async (event) => {
    event.preventDefault();
    if (!selected || !reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/channel/support/tickets/${selected.id}/reply`, { message: reply });
      setReply("");
      await load();
      toast.success("Reply sent to the customer");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const bypassIdentity = async () => {
    if (!selected || !isOwner || !window.confirm("Approve account access for this ticket? This action will be recorded.")) return;
    setBusy(true);
    try {
      await api.post(`/channel/support/tickets/${selected.id}/identity/bypass`, {});
      await load();
      toast.success("Ticket account access approved");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const requestIdentityBypass = async () => {
    if (!selected || !bypassReason.trim()) return;
    setBusy(true);
    try {
      await api.post(`/channel/support/tickets/${selected.id}/identity/bypass/request`, { reason: bypassReason.trim() });
      setBypassReason("");
      await load();
      toast.success("Bypass request sent to the owner");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const requestIdentityVerification = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(`/channel/support/tickets/${selected.id}/identity/request`, {});
      await load();
      toast.success("Account access link sent to the customer");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const accessLinkedAccount = async () => {
    if (!selected || selected.identity_status !== "verified") return;
    setAccountLoading(true);
    try {
      const { data } = await api.get(`/channel/support/tickets/${selected.id}/account`);
      setLinkedAccount(data);
      setAccountEmailDraft(data.email || "");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setAccountLoading(false);
    }
  };

  const saveLinkedAccountEmail = async () => {
    if (!selected || !linkedAccount) return;
    if (!accountEmailDraft.trim()) {
      toast.error("Enter a valid email address");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch(`/channel/support/tickets/${selected.id}/account/email`, { email: accountEmailDraft.trim(), reason: "Support ticket verified account update" });
      setLinkedAccount((current) => ({ ...current, email: data.email }));
      setAccountEmailDraft(data.email || accountEmailDraft.trim());
      toast.success("Customer account email updated");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  if (!selected) {
    return (
      <section className="space-y-5" data-testid="channel-support-inbox">
        <header className="flex flex-wrap items-end justify-between gap-4 border-b border-black/15 pb-4">
          <div>
            <p className="font-mono-label text-xs uppercase tracking-[0.2em] text-[#E62314]">Cresway Channel</p>
            <h2 className="mt-2 font-display text-3xl font-extrabold">Support tickets</h2>
            <p className="mt-1 text-sm text-[#57534E]">{folder === "open" ? openCount : closedCount} {folder} tickets</p>
          </div>
          <button type="button" onClick={load} aria-label="Refresh tickets" title="Refresh tickets" className="border border-black/15 bg-white p-2.5 hover:bg-[#F3F2EF]" data-testid="channel-support-refresh">
            <RefreshCw className="h-4 w-4" />
          </button>
        </header>

        <div className="flex gap-2 border-b border-black/10" role="tablist" aria-label="Ticket folders">
          {[ ["open", "Open", openCount], ["closed", "Closed", closedCount] ].map(([value, label, count]) => <button key={value} type="button" role="tab" aria-selected={folder === value} onClick={() => setFolder(value)} className={`border-b-2 px-4 py-3 text-sm font-bold ${folder === value ? "border-[#E62314] text-[#0A0A0A]" : "border-transparent text-[#57534E] hover:text-black"}`} data-testid={`channel-support-folder-${value}`}>{label} {count}</button>)}
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <label className="flex max-w-xl flex-1 items-center gap-3 border border-black/20 bg-white px-4 py-3">
            <Search className="h-4 w-4 text-[#57534E]" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search reference, name, email or subject" className="w-full bg-transparent text-sm outline-none" data-testid="channel-support-search" />
          </label>
        </div>

        <div className="overflow-hidden border border-black/15 bg-white">
          {visible.map((ticket) => (
            <button
              key={ticket.id}
              type="button"
              onClick={() => setSelectedId(ticket.id)}
              className="block w-full border-b border-black/5 px-5 py-4 text-left transition-colors hover:bg-[#FFF7F5]"
              data-testid={`channel-support-ticket-${ticket.id}`}
            >
              <span className="block min-w-0">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-mono-label text-[11px] text-[#E62314]">{ticket.reference}</span>
                  <span className="text-xs text-[#77736D]">{String(ticket.category).replaceAll("_", " ")}</span>
                  <span className={`border px-2 py-0.5 text-[10px] font-bold uppercase ${ticket.status === "closed" ? "border-black/15 bg-[#F3F2EF] text-[#57534E]" : "border-green-700/20 bg-green-50 text-green-800"}`}>{ticket.status || "open"}</span>
                  {ticket.resolution_status === "resolved" && <span className="border border-blue-700/20 bg-blue-50 px-2 py-0.5 text-[10px] font-bold uppercase text-blue-800">Resolved</span>}
                </span>
                <span className="mt-1 block truncate text-sm font-bold">{ticket.subject}</span>
                <span className="mt-1 block truncate text-xs text-[#57534E]">{ticket.name} · {ticket.email}</span>
              </span>
            </button>
          ))}
          {!visible.length && <p className="p-8 text-center text-sm text-[#57534E]">{folderTickets.length ? "No tickets match your filter." : `No ${folder} tickets.`}</p>}
        </div>
      </section>
    );
  }

  if (linkedAccount) {
    return <ChannelAccountAdmin
      key={`${selected.id}-${linkedAccount.id}`}
      customerId={linkedAccount.id}
      ticketId={selected.id}
      onBack={setLinkedAccount}
    />;
  }

  return (
    <article className="border border-black/15 bg-white" data-testid="channel-support-ticket-file">
      <header className="bg-[#0A0A0A] p-5 text-white sm:p-7">
        <button type="button" onClick={() => setSelectedId("")} className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-white/65 hover:text-white" data-testid="channel-support-back">
          <ArrowLeft className="h-4 w-4" />
          Back to tickets
        </button>
        <div className="flex flex-wrap items-start justify-between gap-5">
          <div>
            <p className="font-mono-label text-xs uppercase tracking-[0.2em] text-[#E62314]">{selected.reference} · {String(selected.category).replaceAll("_", " ")}</p>
            <h2 className="mt-2 break-words font-display text-3xl font-extrabold">{selected.subject}</h2>
            <p className="mt-2 text-sm text-white/60">Submitted by {selected.name} · {selected.email}</p>
            <p className="mt-1 text-xs text-white/45">Opened {new Date(selected.created_at).toLocaleString()}</p>
          </div>
            <div className="flex flex-wrap gap-2">
              <span className={`border px-3 py-1.5 text-xs font-bold uppercase ${selected.status === "closed" ? "border-white/20 bg-white/10 text-white/70" : "border-green-400/40 bg-green-400/10 text-green-200"}`}>{selected.status || "Open"}</span>
              {selected.resolution_status === "resolved" && <span className="border border-blue-400/40 bg-blue-400/10 px-3 py-1.5 text-xs font-bold uppercase text-blue-200">Resolved</span>}
            </div>
        </div>
      </header>

      <div className="grid gap-8 p-5 sm:p-7 lg:grid-cols-[minmax(0,1fr)_240px]">
        <section className="min-w-0">
          <h3 className="mb-4 font-display text-xl font-extrabold">Conversation</h3>
          <ol className="relative space-y-5 border-l-2 border-black/10 pl-5" data-testid="channel-support-messages">
            {(selected.messages || []).map((message, index) => (
              <li key={`${message.created_at}-${index}`} className="relative border border-black/10 bg-[#FBF9F5] p-4">
                <span className={`absolute -left-[27px] top-5 h-3 w-3 border-2 border-white ${message.from === "support" ? "bg-[#E62314]" : "bg-[#0A0A0A]"}`} />
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-bold">{message.from === "support" ? <>Cresway Channel Support <span className="font-normal text-[#57534E]">· {message.name || "Support team"}</span></> : <>Customer <span className="font-normal text-[#57534E]">· {message.name || selected.name}</span></>}</p>
                  <time className="text-xs text-[#77736D]">{new Date(message.created_at).toLocaleString()}</time>
                </div>
                <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-[#3A3836]">{message.message}</p>
              </li>
            ))}
          </ol>

          <form onSubmit={sendReply} className="mt-6 border-t border-black/10 pt-5">
              <label className="block text-sm font-bold">
                Reply to customer
                <textarea required minLength={2} maxLength={5000} rows={5} value={reply} onChange={(e) => setReply(e.target.value)} className="mt-2 block w-full resize-y border border-black/20 px-3 py-3 text-sm font-normal outline-none focus:border-[#E62314]" data-testid="channel-support-staff-reply" />
              </label>
              <button type="submit" disabled={busy} className="mt-4 inline-flex items-center gap-2 bg-[#0A0A0A] px-4 py-3 text-sm font-bold text-white hover:bg-[#E62314] disabled:opacity-50" data-testid="channel-support-staff-send">
                <Send className="h-4 w-4" />
                {busy ? "Sending…" : "Send reply"}
              </button>
          </form>
        </section>

        <aside className="space-y-3 border-t border-black/10 pt-5 lg:border-l lg:border-t-0 lg:pl-5 lg:pt-0">
          <h3 className="font-mono-label text-xs uppercase tracking-[0.18em] text-[#57534E]">Ticket actions</h3>

          <section className="border border-black/10 bg-[#FBF9F5] p-3" data-testid="channel-support-account-access">
            <p className="text-xs font-bold uppercase text-[#57534E]">Customer account</p>
            {accountLoading && <p className="mt-2 text-xs text-[#57534E]">Loading linked account…</p>}
            {linkedAccount && <>
              <dl className="mt-3 space-y-2 border-t border-black/10 pt-3 text-xs"><div><dt className="font-bold text-[#57534E]">Account name</dt><dd className="mt-0.5">{linkedAccount.name || "Not provided"}</dd></div><div><dt className="font-bold text-[#57534E]">Account email</dt><dd className="mt-0.5 break-all">{linkedAccount.email}</dd></div><div><dt className="font-bold text-[#57534E]">Country</dt><dd className="mt-0.5">{linkedAccount.country_code || "Not provided"}</dd></div><div><dt className="font-bold text-[#57534E]">Account status</dt><dd className="mt-0.5">{linkedAccount.status}</dd></div><div><dt className="font-bold text-[#57534E]">Created</dt><dd className="mt-0.5">{linkedAccount.created_at ? new Date(linkedAccount.created_at).toLocaleDateString() : "Unknown"}</dd></div></dl>
              <div className="mt-3 space-y-2 border-t border-black/10 pt-3">
                <label className="block text-xs font-bold text-[#57534E]">Update account email<input type="email" value={accountEmailDraft} onChange={(event) => setAccountEmailDraft(event.target.value)} className="mt-2 w-full border border-black/20 bg-white px-2 py-2 text-xs font-normal" data-testid="channel-support-account-email-input" /></label>
                <button type="button" disabled={busy} onClick={saveLinkedAccountEmail} className="w-full border border-black/20 px-3 py-2 text-xs font-bold hover:border-black disabled:opacity-40" data-testid="channel-support-account-email-save">Save account email</button>
              </div>
            </>}
            {selected.status === "closed" && <p className="mt-3 border-t border-black/10 pt-3 text-xs text-[#57534E]">Ticket closed. Account access through this ticket has ended.</p>}
            {selected.status !== "closed" && selected.identity_status === "verified" && !linkedAccount && <button type="button" disabled={busy || accountLoading} onClick={accessLinkedAccount} className="mt-3 w-full border border-black/20 px-3 py-2 text-xs font-bold hover:border-black disabled:opacity-40" data-testid="channel-support-access-account">{accountLoading ? "Loading account…" : "Access account"}</button>}
            {selected.status !== "closed" && selected.identity_status !== "verified" && selected.identity_status !== "verified_email" && <button type="button" disabled={busy} onClick={requestIdentityVerification} className="mt-3 w-full border border-black/20 px-3 py-2 text-xs font-bold hover:border-black disabled:opacity-40" data-testid="channel-support-request-verification">{selected.identity_status === "pending" ? "Resend account-access link" : "Request account-access link"}</button>}
            {selected.status !== "closed" && selected.identity_status !== "verified" && selected.identity_bypass_request_status === "pending" && <div className="mt-3 border border-[#B42318]/20 bg-white p-3 text-xs" data-testid="channel-support-bypass-pending"><p className="font-bold">Owner approval requested</p><p className="mt-1 text-[#57534E]">By {selected.identity_bypass_requested_by_email || "Support staff"}</p><p className="mt-2 leading-5">{selected.identity_bypass_reason}</p>{isOwner && <button type="button" disabled={busy} onClick={bypassIdentity} className="mt-3 w-full border border-[#B42318]/40 px-3 py-2 font-bold text-[#B42318] hover:bg-[#FFF1EF] disabled:opacity-40" data-testid="channel-support-identity-bypass-approve">Approve account access</button>}</div>}
            {selected.status !== "closed" && !isOwner && selected.identity_status !== "verified" && selected.identity_bypass_request_status !== "pending" && <div className="mt-3 space-y-2"><label className="block text-xs font-bold">Request owner approval<textarea maxLength={500} rows={3} value={bypassReason} onChange={(event) => setBypassReason(event.target.value)} className="mt-2 w-full border border-black/20 bg-white px-2 py-2 text-xs font-normal" placeholder="Explain why account access is needed" data-testid="channel-support-bypass-reason" /></label><button type="button" disabled={busy || bypassReason.trim().length < 5} onClick={requestIdentityBypass} className="w-full border border-black/20 px-3 py-2 text-xs font-bold hover:border-black disabled:opacity-40" data-testid="channel-support-identity-bypass-request">Request account access approval</button></div>}
          </section>

          <button type="button" disabled={busy} onClick={sendUpdateEmail} className="flex w-full items-center justify-center gap-2 border border-[#E62314]/30 bg-[#FFF1EF] px-3 py-3 text-sm font-bold text-[#B51F13] hover:border-[#E62314] disabled:opacity-40" data-testid="channel-support-send-update">
            <Send className="h-4 w-4" />
            Send case update
          </button>
          <p className="text-[11px] leading-5 text-[#57534E]">Sends the standard “there’s an update on your support ticket” email with a link back to the case.</p>

          {selected.status === "closed" ? <div className="space-y-2"><button type="button" disabled={busy} onClick={() => updateStatus("reopen")} className="w-full border border-black/20 px-3 py-3 text-sm font-bold hover:border-[#E62314] disabled:opacity-40" data-testid="channel-support-reopen">Reopen ticket</button><button type="button" disabled={busy} onClick={deleteTicket} className="flex w-full items-center justify-center gap-2 border border-[#B42318]/40 px-3 py-3 text-sm font-bold text-[#B42318] hover:bg-[#FFF1EF] disabled:opacity-40" data-testid="channel-support-delete"><Trash2 className="h-4 w-4" />Delete ticket</button></div>
            : <div className="space-y-2">
              {selected.resolution_status !== "resolved" && <button type="button" disabled={busy} onClick={() => updateStatus("resolve")} className="w-full border border-blue-700/30 bg-blue-50 px-3 py-3 text-sm font-bold text-blue-800 hover:bg-blue-100 disabled:opacity-40" data-testid="channel-support-resolve">Mark resolved</button>}
              <button type="button" disabled={busy} onClick={() => updateStatus("close")} className="w-full border border-black/20 px-3 py-3 text-sm font-bold hover:border-[#E62314] disabled:opacity-40" data-testid="channel-support-close">Close ticket</button>
            </div>}
        </aside>
      </div>
    </article>
  );
};
